import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ChevronRight, ChevronLeft, Sparkles, ShoppingCart, CalendarClock, PartyPopper, Flame } from "lucide-react";

const themes = [
  { id: "warm", label: "Calda Toscana", swatch: ["#C85A32","#D99B26","#FBF9F5"] },
  { id: "elegant", label: "Eleganza Rustica", swatch: ["#3B503A","#EAF0E9","#FBF9F5"] },
  { id: "dark", label: "Osteria Notte", swatch: ["#161412","#D99B26","#231F1D"] },
];

function FeatureToggle({ icon: Icon, title, desc, value, onChange, testid }) {
  return (
    <button data-testid={testid} onClick={() => onChange(!value)} className={`w-full text-left rounded-2xl border-2 p-4 flex items-start gap-3 transition ${value ? "border-[#C85A32] bg-[#FDECE4]" : "border-[#E8E2D5] bg-white hover:border-[#C85A32]/40"}`}>
      <div className={`w-10 h-10 rounded-full grid place-items-center flex-shrink-0 ${value ? "bg-[#C85A32] text-white" : "bg-[#F0EBE1] text-[#6B625B]"}`}><Icon size={18}/></div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-sm">{title}</div>
        <div className="text-xs text-[#6B625B] mt-0.5">{desc}</div>
      </div>
      <div className={`w-10 h-6 rounded-full flex-shrink-0 relative transition ${value ? "bg-[#3B503A]" : "bg-[#E8E2D5]"}`}>
        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${value ? "left-[18px]" : "left-0.5"}`}/>
      </div>
    </button>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", cuisine_type: "Italiana", theme: "warm", description: "",
    ordering_enabled: true, takeaway_enabled: false, delivery_enabled: false, party_mode_enabled: true, daily_menu_enabled: false,
  });

  useEffect(() => {
    api.get("/restaurant").then(({ data }) => setForm({
      name: data.name || "",
      cuisine_type: data.cuisine_type || "Italiana",
      theme: data.theme || "warm",
      description: data.description || "",
      ordering_enabled: data.ordering_enabled !== false,
      takeaway_enabled: data.takeaway_enabled === true,
      delivery_enabled: data.delivery_enabled === true,
      party_mode_enabled: data.party_mode_enabled !== false,
      daily_menu_enabled: data.daily_menu_enabled === true,
    })).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await api.put("/restaurant", form);
      toast.success("Preferenze salvate");
      navigate("/dashboard");
    } catch { toast.error("Errore salvataggio"); }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F5] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl bg-white rounded-3xl border border-[#E8E2D5] p-6 sm:p-8 shadow-sm">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#6B625B] font-semibold">
          <Sparkles size={14} className="text-[#C85A32]"/> Step {step} di 4
        </div>
        <div className="mt-2 h-1.5 bg-[#F0EBE1] rounded-full overflow-hidden">
          <div className="h-full bg-[#C85A32] transition-all" style={{ width: `${(step/4)*100}%` }}/>
        </div>

        {step === 1 && (
          <div className="mt-8 fade-in-up">
            <h2 className="font-serif-display text-3xl font-bold">Come si chiama il tuo locale?</h2>
            <p className="text-[#6B625B] mt-1">Il nome apparirà sul menu digitale scansionato dai clienti.</p>
            <div className="mt-6 space-y-4">
              <div><Label>Nome ristorante</Label><Input data-testid="onboard-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Osteria del Sole"/></div>
              <div><Label>Tipo di cucina</Label><Input data-testid="onboard-cuisine-input" value={form.cuisine_type} onChange={(e) => setForm({...form, cuisine_type: e.target.value})} placeholder="Toscana, Pizzeria, Fusion..."/></div>
              <div><Label>Presentazione breve</Label><Input data-testid="onboard-desc-input" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Cucina di casa dal 1978"/></div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-8 fade-in-up">
            <h2 className="font-serif-display text-3xl font-bold">Scegli lo stile del menu</h2>
            <p className="text-[#6B625B] mt-1">Puoi cambiarlo in qualsiasi momento con la voce.</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {themes.map((t) => (
                <button key={t.id} data-testid={`onboard-theme-${t.id}`} onClick={() => setForm({...form, theme: t.id})}
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${form.theme === t.id ? "border-[#C85A32] bg-[#FDECE4]" : "border-[#E8E2D5] hover:border-[#C85A32]/50"}`}>
                  <div className="flex gap-1">{t.swatch.map((c, i) => <div key={i} className="w-6 h-6 rounded-full border border-[#E8E2D5]" style={{ background: c }}/>)}</div>
                  <div className="mt-3 font-heading font-semibold">{t.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-8 fade-in-up">
            <h2 className="font-serif-display text-3xl font-bold">Funzionalità del locale</h2>
            <p className="text-[#6B625B] mt-1">Attiva o disattiva le funzioni per i tuoi clienti. Puoi cambiare in qualsiasi momento.</p>
            <div className="mt-6 space-y-3">
              <FeatureToggle testid="toggle-ordering" icon={ShoppingCart} title="Ordini al tavolo"
                desc="I clienti registrati al tavolo aggiungono piatti al carrello e mandano l'ordine in cucina."
                value={form.ordering_enabled} onChange={(v) => setForm({...form, ordering_enabled: v})}/>
              <FeatureToggle testid="toggle-takeaway" icon={ShoppingCart} title="Asporto"
                desc="Genera un QR/link dedicato per far ordinare i clienti che vengono a ritirare. Pagamento in contanti al ritiro."
                value={form.takeaway_enabled} onChange={(v) => setForm({...form, takeaway_enabled: v})}/>
              <FeatureToggle testid="toggle-delivery" icon={ShoppingCart} title="Consegna a domicilio (Delivery)"
                desc="Genera un QR/link dedicato per l'ordine con consegna. Il cliente lascia indirizzo e telefono, paga in contanti alla consegna."
                value={form.delivery_enabled} onChange={(v) => setForm({...form, delivery_enabled: v})}/>
              <FeatureToggle testid="toggle-party" icon={PartyPopper} title="Menuparty (quiz + chat)"
                desc="Attiva il torneo quiz, la chat del locale e le DM tra tavoli per intrattenere i clienti."
                value={form.party_mode_enabled} onChange={(v) => setForm({...form, party_mode_enabled: v})}/>
              <FeatureToggle testid="toggle-daily" icon={Flame} title="Menu del giorno"
                desc="Sezione speciale in cima al menu con i piatti in evidenza della giornata."
                value={form.daily_menu_enabled} onChange={(v) => setForm({...form, daily_menu_enabled: v})}/>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="mt-8 fade-in-up">
            <h2 className="font-serif-display text-3xl font-bold">Sei pronto!</h2>
            <p className="text-[#6B625B] mt-1">Tra un attimo aggiungerai il tuo primo piatto parlando al microfono.</p>
            <div className="mt-6 rounded-2xl bg-[#EAF0E9] border border-[#D6E1D4] p-6">
              <div className="text-xs uppercase tracking-wider text-[#3B503A] font-semibold">Prova a dire</div>
              <div className="mt-2 font-serif-display text-xl font-bold text-[#3B503A] italic">"Aggiungi Spaghetti alle Vongole a 15 euro nei Primi"</div>
            </div>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <Button data-testid="onboard-back-btn" variant="outline" onClick={() => setStep((s) => Math.max(1, s-1))} disabled={step === 1} className="rounded-full">
            <ChevronLeft size={16} className="mr-1"/> Indietro
          </Button>
          {step < 4 ? (
            <Button data-testid="onboarding-next-btn" onClick={() => setStep(step+1)} className="bg-[#C85A32] hover:bg-[#B24E2A] text-white rounded-full">
              Avanti <ChevronRight size={16} className="ml-1"/>
            </Button>
          ) : (
            <Button data-testid="onboard-finish-btn" onClick={save} className="bg-[#3B503A] hover:bg-[#2f4030] text-white rounded-full">
              Vai alla dashboard <ChevronRight size={16} className="ml-1"/>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
